import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { useApiFallback } from "../../hooks/useApiFallback";
import { getLocalServices } from "../../api/endpoints";
import type { LocalServiceOut } from "../../api/types";
import { localServices as mockServices } from "../../data";

// Fallback берём из старых mock — переводим в форму бэкенда (name/pid/description/status).
const FALLBACK_SERVICES: LocalServiceOut[] = mockServices.map((s) => ({
  name: s.id,
  status: s.status === "running" ? "running" : s.status === "down" ? "failed" : "running",
  pid: null,
  description: s.description,
}));

type UiStatus = "running" | "failed" | "stopped";
function toUiStatus(s: string): UiStatus {
  if (s === "running") return "running";
  if (s === "failed") return "failed";
  return "stopped";
}

export default function ServicesPage() {
  const navigate = useNavigate();
  const push = useToast();
  const [refreshing, setRefreshing] = useState(false);

  const servicesQ = useQuery({
    queryKey: ["local", "services"],
    queryFn: getLocalServices,
    refetchInterval: 15_000,
  });
  const services = useApiFallback<LocalServiceOut[]>(
    servicesQ.data,
    servicesQ.isError,
    FALLBACK_SERVICES,
  );

  const refresh = async () => {
    setRefreshing(true);
    await servicesQ.refetch();
    setRefreshing(false);
    push({ kind: "success", text: "Статусы сервисов обновлены" });
  };

  const items = services.value;
  const runningCount = items.filter((s) => s.status === "running").length;
  const failedCount = items.filter((s) => s.status === "failed").length;

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Сервисы узла
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--color-fg-muted)]">
            <SummaryPill kind="ok">{runningCount} running</SummaryPill>
            {failedCount > 0 && <SummaryPill kind="err">{failedCount} failed</SummaryPill>}
            {servicesQ.isFetching && <span>· обновляется…</span>}
          </div>
        </div>
        <Btn icon={RefreshCw} iconClass={refreshing ? "spin" : ""} onClick={refresh}>
          {refreshing ? "Обновление…" : "Обновить статусы"}
        </Btn>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {items.map((s, i) => {
          const ui = toUiStatus(s.status);
          return (
            <section
              key={s.name}
              style={{ animationDelay: `${i * 35}ms` }}
              className="stagger-up rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <ServiceDot status={ui} />
                  <div>
                    <div className="font-mono text-[14px] font-semibold text-[var(--color-fg)]">{s.name}</div>
                    {s.description && (
                      <div className="mt-0.5 text-[12px] text-[var(--color-fg-muted)]">{s.description}</div>
                    )}
                  </div>
                </div>
                <Status status={ui} />
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                <Field
                  label="PID"
                  value={<span className="font-mono">{s.pid !== null ? s.pid : "—"}</span>}
                />
                <Field label="Тип" value={typeFor(s.name)} />
              </dl>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => navigate("/local/log")}
                  className="ml-auto self-center text-[12px] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
                >
                  логи →
                </button>
              </div>
            </section>
          );
        })}
        {!items.length && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-6 text-center text-[12.5px] text-[var(--color-fg-muted)] md:col-span-2">
            Сервисов нет (или /local/services пуст).
          </div>
        )}
      </div>
    </>
  );
}

function typeFor(name: string): string {
  if (name === "postgresql" || name === "postgres") return "База данных";
  if (name === "nginx") return "Web/HTTP";
  if (name.includes("astramarket")) return "Демон астрамаркета";
  return "Демон";
}

function ServiceDot({ status }: { status: UiStatus }) {
  const color =
    status === "running" ? "var(--color-success)" :
    status === "failed"  ? "var(--color-danger)" :
                           "var(--color-warning)";
  return (
    <span className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
  );
}

function Status({ status }: { status: UiStatus }) {
  const map = {
    running: { bg: "var(--color-success-bg)", fg: "var(--color-success)", text: "RUNNING" },
    failed:  { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)",  text: "FAILED"  },
    stopped: { bg: "var(--color-warning-bg)", fg: "var(--color-warning)", text: "STOPPED" },
  };
  const m = map[status];
  return (
    <span
      className="shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.text}
    </span>
  );
}

function SummaryPill({ kind, children }: { kind: "ok" | "err"; children: React.ReactNode }) {
  const m = kind === "ok"
    ? { bg: "var(--color-success-bg)", fg: "var(--color-success)" }
    : { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)" };
  return (
    <span className="rounded-full px-1.5 py-0.5 text-[10.5px] font-medium" style={{ background: m.bg, color: m.fg }}>
      {children}
    </span>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">{label}</div>
      <div className="mt-0.5 text-[var(--color-fg)]">{value}</div>
    </div>
  );
}
